import { Record } from "@deepstream/client/dist/src/record/record";
import axios from "axios";
import { ownershipMergeStrategy } from "src/utils/deepstream-merge";

/**
 * The agent's preview (this app in preview mode) only authors the interaction
 * state. Keep it on conflict; take everything else from remote.
 */
const PREVIEW_OWNED_PATHS = ["sync.currentInteractionState"];

export default class Preview {
  constructor(deepstream, store) {
    this.store = store;
    this.deepstream = deepstream;
    /**
     * @type {Record}
     */
    this.record = null;
    this.meeting = null;
  }
  async join(recordName) {
    this.record = this.deepstream.client.record.getRecord(recordName);
    await this.record.whenReady();
    this.record.unsubscribe();
    // Was effectively LOCAL_WINS (the first arg is localValue, not the Record) —
    // which clobbered remote fields. Use field-ownership merge instead.
    this.record.setMergeStrategy(ownershipMergeStrategy(PREVIEW_OWNED_PATHS));
    this.unsubscribe();
    this.meeting = await this.record.get();
    if (!this.meeting) {
      throw new Error("Session no longer active");
    }
    this.subscribe();
  }

  subscribe() {
    // NOTE: interactive state (sync.currentInteractionState) moved to the
    // 'interaction-state' EVENT (see Preview.vue → subscribeInteractionState).

    //subscribe currentInteraction
    this.record.subscribe(
      "sync.currentInteraction",
      this.syncCurrentInteraction.bind(this),
      true
    );

    //subscribe standby
    this.record.subscribe("sync.standby", this.syncStandby.bind(this), true);
  }

  unsubscribe() {
    this.record.unsubscribe(
      "sync.currentInteraction",
      this.syncCurrentInteraction.bind(this)
    );

    this.record.unsubscribe("sync.standby", this.syncStandby.bind(this));
  }

  async syncCurrentInteraction(currentInteraction) {
    if (currentInteraction?.source === "plugin") {
      await this.setCurrentInteractionFormatVersion1(currentInteraction);
      return;
    }

    // Cards API Versioning & Routing
    // -------------------------------------------------------
    // cardsVersion determines which backend service handles the cards/getCard request.
    //
    //   cardsVersion === 2 -> CORE_DATA_SERVICES_API_URL (data-services app in cenora-core-data-services)
    //   cardsVersion === 3 -> AGENT_DATA_SERVICES_API_URL (agency-data-services app in cenora-core-data-services)
    //
    // Both services expose the same cards/getCard endpoint.
    // The env var controls which service is called — no version prefix in the URL.
    if (this.meeting?.cardsVersion === 3) {
      await this.setCurrentInteractionFormatVersion3(currentInteraction);
      return;
    }

    if (this.meeting?.cardsVersion === 2) {
      await this.setCurrentInteractionFormatVersion2(currentInteraction);
      return;
    }

    await this.setCurrentInteractionFormatVersion1(currentInteraction);
  }

  syncStandby(standby) {
    this.store.commit("meeting/standby", standby);
  }

  async setCurrentInteractionFormatVersion1(currentInteraction) {
    this.store.commit("meeting/currentInteraction", currentInteraction);
  }

  async setCurrentInteractionFormatVersion2(currentInteraction) {
    if (!currentInteraction) {
      this.store.commit("meeting/currentInteraction", null);
      return;
    }

    if (
      currentInteraction.type === "card" &&
      !currentInteraction?.content?.hash_id
    ) {
      this.store.commit("meeting/currentInteraction", null);
      return;
    }

    if (currentInteraction.type !== "card") {
      this.store.commit("meeting/currentInteraction", currentInteraction);
      return;
    }

    let baseUrl = process.env.CORE_DATA_SERVICES_API_URL;

    if (
      !!currentInteraction.content.industry &&
      currentInteraction.content.industry !== "cruiseline"
    ) {
      baseUrl += `/${currentInteraction.content.industry}`;
    }

    const response = await axios.post(`${baseUrl}/cards/getCard`, {
      hash_ids: currentInteraction.content.hash_id,
      parent_hash_id: currentInteraction.content.parentMeta?.hash_id,
    });
    this.store.commit("meeting/currentInteraction", {
      ...currentInteraction,
      content:
        response.data instanceof Array ? response.data[0] : response.data,
    });
  }

  async setCurrentInteractionFormatVersion3(currentInteraction) {
    if (!currentInteraction) {
      this.store.commit("meeting/currentInteraction", null);
      return;
    }

    if (
      currentInteraction.type === "card" &&
      !currentInteraction?.content?.hash_id
    ) {
      this.store.commit("meeting/currentInteraction", null);
      return;
    }

    if (currentInteraction.type !== "card") {
      this.store.commit("meeting/currentInteraction", currentInteraction);
      return;
    }

    let baseUrl = process.env.AGENT_DATA_SERVICES_API_URL;

    // Industry-based URL prefixing is disabled for v3 — agency-data-services
    // handles all industries at a single endpoint without a path prefix.
    // if (
    //   !!currentInteraction.content.industry &&
    //   currentInteraction.content.industry !== "cruiseline"
    // ) {
    //   baseUrl += `/${currentInteraction.content.industry}`;
    // }

    const response = await axios.post(`${baseUrl}/cards/getCard`, {
      hash_ids: currentInteraction.content.hash_id,
      parent_hash_id: currentInteraction.content.parentMeta?.hash_id,
    });
    this.store.commit("meeting/currentInteraction", {
      ...currentInteraction,
      content:
        response.data instanceof Array ? response.data[0] : response.data,
    });
  }

  // --- Realtime interactive state via EVENTS (pub/sub), not records ---
  // This is the agent's preview side (origin: 'agent').

  emitInteractionState(state) {
    if (!this.record) return;
    this.lastInteractionState = state;
    this.deepstream.client.event.emit(`interaction-state/${this.record.name}`, {
      origin: "agent",
      state,
    });
  }

  subscribeInteractionState(applyFn) {
    if (!this.record) return;
    const name = this.record.name;
    this.deepstream.client.event.subscribe(
      `interaction-state/${name}`,
      (payload) => {
        if (!payload || payload.origin === "agent") return;
        this.lastInteractionState = payload.state;
        applyFn(payload.state);
      }
    );
    this.deepstream.client.event.subscribe(
      `request-interaction-state/${name}`,
      (payload) => {
        if (!payload || payload.origin === "agent") return;
        if (
          this.lastInteractionState !== undefined &&
          this.lastInteractionState !== null
        ) {
          this.emitInteractionState(this.lastInteractionState);
        }
      }
    );
  }

  requestInteractionState() {
    if (!this.record) return;
    this.deepstream.client.event.emit(
      `request-interaction-state/${this.record.name}`,
      { origin: "agent" }
    );
  }

  // Open/close of the business card from the agent's preview → agent via event
  // (consumer→agent signal; keeps the record single-writer).
  emitOpenBusinessCard(value) {
    if (!this.record) return;
    this.deepstream.client.event.emit(
      `open-business-card/${this.record.name}`,
      value
    );
  }

  async cardStateChanged(state) {
    this.emitInteractionState(state);
  }

  async logout() {
    this.store.commit("meeting/reset");
    this.deepstream.logout();
    document.location = "/widget";
  }
}
